import { PerformanceAssessmentDocumentationMode } from '@/api/generated/api.enum';
import { getAssessmentDocumentationMode } from 'common/utils/performance.assessment.utils';
import type { Components } from '@/api/generated/api';
import { useCompetenceObservationMutations } from '@/composables/competenceObservation/useCompetenceObservationMutations';
import { usePerformanceAssessmentCollectiveScope } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentCollectiveScope';
import { usePerformanceAssessmentStudentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentStudentEntryMutations';
import type { Ref } from 'vue';
import { assertArrayNotEmpty } from '@/utils/assertion.utils';
import type { ICollectiveAssessmentPayload } from './performanceAssessmentCollectiveAssessment.types';

type TRatableEntity = { id: number; performanceLevelV2Id: number | null; gradeValue?: number | null };

interface IPerformanceAssessmentCollectiveAssessmentComposable {
  applyCollectiveAssessment: (payload: ICollectiveAssessmentPayload) => Promise<void>;
}

export const usePerformanceAssessmentCollectiveAssessment = (params: {
  disabled: Ref<boolean>;
  entry: Ref<Components.Schemas.PerformanceAssessmentEntryDto | null>;
  refreshEntry: () => Promise<void>;
}): IPerformanceAssessmentCollectiveAssessmentComposable => {
  const { bulkUpdatePerformanceAssessmentStudentEntries } = usePerformanceAssessmentStudentEntryMutations();
  const { bulkUpdateCompetenceObservationLevels } = useCompetenceObservationMutations();
  const { getRatingScope } = usePerformanceAssessmentCollectiveScope();

  const getRelevantRatingIds = async <T extends TRatableEntity>(entities: T[]): Promise<number[]> => {
    const scope = await getRatingScope(entities);
    if (scope === 'all') return entities.map((entity) => entity.id);
    if (scope === 'missing') {
      return entities
        .filter((entity) => entity.performanceLevelV2Id == null && entity.gradeValue == null)
        .map((entity) => entity.id);
    }
    return [];
  };

  const applyCollectiveAssessment = async (payload: ICollectiveAssessmentPayload): Promise<void> => {
    const entry = params.entry.value;
    if (!entry || params.disabled.value) return;
    const isSchoolGrade =
      getAssessmentDocumentationMode(entry.performanceAssessment) ===
      PerformanceAssessmentDocumentationMode.SCHOOL_GRADE;
    if (payload.target === 'performanceAssessment') {
      // An empty collective selection means no grade was chosen, never a request to erase grades.
      if (isSchoolGrade && payload.gradeValue == null) return;
      const ids = await getRelevantRatingIds(entry.studentEntries);
      if (ids.length) {
        await bulkUpdatePerformanceAssessmentStudentEntries({
          schoolClassId: entry.schoolClassId,
          performanceAssessmentEntryId: entry.id,
          studentEntryIds: ids,
          dto: isSchoolGrade
            ? { gradeValue: payload.gradeValue }
            : { performanceLevelV2Id: payload.performanceLevelV2Id },
        });
      }
    } else if (payload.competenceIds.length) {
      const competenceIds = new Set(payload.competenceIds);
      const observations = entry.competenceObservations.filter((observation) =>
        competenceIds.has(observation.competenceId),
      );
      // one scope prompt for the whole selection, so the answer applies to every selected competence
      const ids = await getRelevantRatingIds(observations);
      if (ids.length) {
        await bulkUpdateCompetenceObservationLevels({
          schoolClassId: entry.schoolClassId,
          performanceAssessmentEntryId: entry.id,
          observationIds: assertArrayNotEmpty(ids),
          performanceLevelV2Id: payload.performanceLevelV2Id,
        });
      }
    }
    await params.refreshEntry();
  };

  return {
    applyCollectiveAssessment,
  };
};
